/**
 * AiCreditsCard — the customer's AI-credit wallet + top-up.
 *
 * Shows what they can spend now, how much of it expires with the month, and
 * the packs they can buy. "Buy" opens the gateway checkout (Stripe / Razorpay)
 * in the same tab; the webhook grants the credits and the wallet query
 * refetches when they return (`?credits=success`).
 */

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Coins, Sparkles, Loader2 } from 'lucide-react';
import { aiCreditHelpers, type AiCreditWallet } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { apiErrorMessage } from '@/lib/conflict';
import { formatMoney } from '@/lib/money';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export function useAiCredits() {
  return useQuery({ queryKey: qk.aiCredits(), queryFn: aiCreditHelpers.wallet, staleTime: 15_000 });
}

export function TopUpDialog({
  open,
  onOpenChange,
  wallet,
  reason,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  wallet?: AiCreditWallet;
  reason?: string;
}) {
  const location = useLocation();
  const [busy, setBusy] = useState<string | null>(null);
  const checkout = useMutation({
    mutationFn: (packId: string) => aiCreditHelpers.checkout(packId, location.pathname),
    onMutate: (packId) => setBusy(packId),
    onSuccess: (data) => {
      window.location.assign(data.checkoutUrl);
    },
    onError: (err) => {
      setBusy(null);
      toast.error(apiErrorMessage(err, 'Could not start checkout'));
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5 text-amber-500" /> Top up AI credits
          </DialogTitle>
          <DialogDescription>
            {reason ||
              'AI credits pay for building and editing your website with AI. Purchased credits never expire.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {(wallet?.packs ?? []).map((pack) => (
            <button
              key={pack.id}
              disabled={busy !== null}
              onClick={() => checkout.mutate(pack.id)}
              className="hover:border-primary flex w-full items-center justify-between rounded-lg border p-4 text-left transition-colors disabled:opacity-60"
            >
              <div>
                <p className="font-medium">{pack.credits.toLocaleString()} credits</p>
                <p className="text-muted-foreground text-xs">{pack.label}</p>
              </div>
              <div className="flex items-center gap-2 font-semibold">
                {busy === pack.id ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {formatMoney(pack.price, pack.currency)}
              </div>
            </button>
          ))}
          {!wallet?.packs?.length && (
            <p className="text-muted-foreground text-sm">
              Credit packs are not available right now.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Not now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AiCreditsCard({ compact = false }: { compact?: boolean }) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const wallet = useAiCredits();
  const [topUp, setTopUp] = useState(false);

  // Back from checkout: refresh and say thanks (one-shot).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const state = params.get('credits');
    if (!state) return;
    if (state === 'success') {
      toast.success('Payment received — your credits will appear in a moment.');
      queryClient.invalidateQueries({ queryKey: qk.aiCredits() });
      setTimeout(() => queryClient.invalidateQueries({ queryKey: qk.aiCredits() }), 4000);
    } else if (state === 'cancelled') {
      toast.info('Checkout cancelled.');
    }
    params.delete('credits');
    const qs = params.toString();
    window.history.replaceState({}, '', `${location.pathname}${qs ? `?${qs}` : ''}`);
  }, [location.search, location.pathname, queryClient]);

  const w = wallet.data;
  const expiresOn = w
    ? new Date(w.expiresAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : '';

  return (
    <div className={`bg-card rounded-xl border ${compact ? 'p-4' : 'p-5'}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide">
            <Sparkles className="h-3.5 w-3.5" /> AI credits
          </p>
          <p className="mt-1 text-2xl font-bold">
            {w ? w.available.toLocaleString() : '—'}
            <span className="text-muted-foreground ml-1 text-sm font-normal">available</span>
          </p>
          {w && w.expiring > 0 && (
            <p className="text-muted-foreground mt-0.5 text-xs">
              {w.expiring.toLocaleString()} of these expire on {expiresOn}
              {w.purchased > 0 ? ` · ${w.purchased.toLocaleString()} never expire` : ''}
            </p>
          )}
          {w && w.reserved > 0 && (
            <p className="text-muted-foreground mt-0.5 text-xs">
              {w.reserved.toLocaleString()} held by a run in progress
            </p>
          )}
          {w && w.available === 0 && w.expiring === 0 && (
            <p className="mt-0.5 text-xs text-amber-600">
              You have no credits left — top up to keep building.
            </p>
          )}
        </div>
        <Button size="sm" onClick={() => setTopUp(true)}>
          Top up
        </Button>
      </div>
      {!compact && w && (
        <p className="text-muted-foreground mt-3 text-xs">
          Building a website needs about {w.reserveFor.generate.toLocaleString()} credits to start;
          an AI edit about {w.reserveFor.edit.toLocaleString()}. You are only charged what a run
          actually uses.
        </p>
      )}
      <TopUpDialog open={topUp} onOpenChange={setTopUp} wallet={w} />
    </div>
  );
}
